import { applicationContext } from 'core/utils';
import { action, observable, runInAction } from 'mobx';
import { ModalParams } from 'core/models';
import { BaasicDropdownStore, BaseEditViewStore } from 'core/stores';
import { UserEditForm } from 'application/user/forms';
import { CoreUserService } from 'application/user/services';
import { TeamsService } from "application/teams/services";

@applicationContext
class UserEditViewStore extends BaseEditViewStore {
    @observable accessor userProfile = null;    

    passwordChangeForm = null;
    lockConfirm = null;
    approveConfirm = null;
    mailPasswordResetConfirm = null;
    changePassword = null;
    titleDropdownStore = null;
    languageDropdownStore = null;
    constructor(rootStore) {
        const service = new CoreUserService(rootStore.application.baasic.apiClient);
        const teamsService = new TeamsService(
                    rootStore.application.baasic.apiClient
                );

        const id = rootStore.routerStore.routerState.params.id;
        super(rootStore, {
            name: 'user',
            autoInit: false,
            id: id,
            actions: {
                get: async (id) => {
                    const response = await rootStore.application.baasic.membershipModule.user.get(id, { embed: 'roles' });
                    return response.data;
                }
            },
            FormClass: UserEditForm,
        });

        this.service = service;
        this.teamsService = teamsService;
        this.userId=id;
        this.changePasswordModal = new ModalParams({});
        this.roleMultiselectStore = new BaasicDropdownStore({
            multi: true,
        }, {
            fetchFunc: async () => {
                const response = await this.rootStore.application.baasic.membershipModule.lookups.get({ embed: 'role', rpp: 30 });
                return [...response.data.role].sort((a, b) => a.name > b.name ? 1 : -1, );
            },
            onChange: (roles)=>{
                this.item.roles = roles;
                this.form.set({ roles: roles });
            }
        });

        this.emailSignatureEditModal = new ModalParams({});
    }    

    @action.bound
    async onInit({ initialLoad }) {
        if (!initialLoad) {
            this.rootStore.routerStore.goTo(
                'master.app.main.user.list'
            )
        }
        else {
            this.form.clear();
            await this.fetchRoles();
            await this.fetch([
                this.getResource(this.id),
            ]);
        }
    }

    @action.bound
    async getResource(id) {
        await super.getResource(id);

        runInAction(() => {
            this.form.$('roles').set(this.item.roles);
            this.form.validate();
        });
    }

    @action.bound
    async updateResource(resource) {
        const { roles, userName, email, firstName, lastName, displayName } = resource; // eslint-disable-line
        const currentUser = this.rootStore.userStore.user;
        const isCurrentUser = !!currentUser && `${currentUser.id}` === `${this.id}`;
        const usernameChanged = this.item.userName !== userName;
        const user = {
            ...this.item,
            email,
            roles,
            firstName,
            lastName,
            displayName,
            userName
        };
        try {
            // fetch resolves all promises, not only GET requests
            this.fetch([
                await this.service.update({
                    id: this.id,
                    ...user
                })
            ]);

            // Changing your own username invalidates the current access token server-side
            // (the token is bound to the old username). The stale token still looks valid
            // locally, so force a re-login instead of navigating back into the app with a
            // dead token, which would 401 and route to the "unauthorized" page.
            if (isCurrentUser && usernameChanged) {
                this.forceReLogin();
                return;
            }

            this.rootStore.notificationStore.success('USER.EDIT.SUCCESS');
            await this.rootStore.routerStore.goBack();
        }
        catch (err) {
            this.rootStore.notificationStore.error(err.data && err.data.errors ?  err.data.errors : "USER.EDIT.ERROR");
        }
    }

    @action.bound
    forceReLogin() {
        this.rootStore.application.baasic.updateAccessToken(null);
        this.rootStore.authStore.syncToken();
        this.rootStore.notificationStore.success('USER.EDIT.USERNAME_CHANGED_RELOGIN');
        this.rootStore.navigateLogin();
    }

    @action.bound
    async fetchRoles() {
        this.roleMultiselectStore.setLoading(true);
        const response = await this.rootStore.application.baasic.membershipModule.lookups.get({ embed: 'role', rpp: 30 });
        runInAction(() => {
            this.roleMultiselectStore.setItems([...response.data.role].sort((a, b) => a.name > b.name ? 1 : -1, ));
            this.roleMultiselectStore.setLoading(false);
        });
    }

    @action.bound
    async toggleLock() {
        this.rootStore.modalStore.showConfirm(
            `Are you sure you want to ${
                this.item.isLockedOut ? 'unlock' : 'lock'
                } user?`,
            async () => {
                if (this.item.isLockedOut) {
                    await this.rootStore.application.baasic.membershipModule.user.unlock(this.item);
                } else {
                    await this.rootStore.application.baasic.membershipModule.user.lock(this.item);
                }
                await this.getResource(this.item.id);
                this.rootStore.notificationStore.success(
                    `Successfully ${
                        this.item.isLockedOut ? 'locked' : 'unlocked'
                        } user`
                );
            }
        );
    }

    @action.bound
    async toggleApprove() {
        this.rootStore.modalStore.showConfirm(
            `Are you sure you want to ${
                this.item.isApproved ? 'disapprove' : 'approve'
                } user?`,
            async () => {
                this.loaderStore.suspend();
                if (this.item.isApproved) {
                    await this.rootStore.application.baasic.membershipModule.user.disapprove(this.item);
                    await this.teamsService.deleteTeamUsers({userId: this.item.id});
                } else {
                    await this.rootStore.application.baasic.membershipModule.user.approve(this.item);
                }

                await this.getResource(this.item.id);
                this.rootStore.notificationStore.success(
                    `User successfully ${
                        this.item.isApproved ? 'approved.' : 'disapproved.'
                        }`
                );
                this.loaderStore.resume();
            }
        );
    }

    @action.bound
    async openMailPasswordReset() {
        this.rootStore.modalStore.showConfirm(
            'Send password reset email to ' + this.item.email,
            async () => {
                this.loaderStore.suspend();
                try {
                    const recoverUrl = `${window.location.origin}/password-change/{?passwordRecoveryToken}`;
                    await this.rootStore.application.baasic.membershipModule.passwordRecovery.requestReset({
                        userName: this.item.email,
                        recoverUrl: recoverUrl
                    });

                    this.rootStore.notificationStore.success('Password reset mail sent to ' + this.item.email);
                }
                catch (err) {
                    this.rootStore.notificationStore.error('Failed to send password reset mail to ' + this.item.email);
                }
                finally {
                    this.loaderStore.resume();
                }
            }
        )
    }

    @action.bound
    openChangePassword() {
        this.changePasswordModal.open();
    }

    @action.bound
    async openChangeEmailSignature() {
        var response = await this.service.get(this.userId, {embed: 'userSetting'});
        this.emailSignatureEditModal.open({
            userId: this.userId,
            userSetting: response.data.userSetting
        });
    }
}

export default UserEditViewStore;
